/**
 * Product List Filters Storage Service
 *
 * Owns all raw `localStorage` access for the `ibe_product_list_filters` key —
 * the persisted search/filter selections that let a user carry their dates,
 * occupancy, destination, sorting and accommodation filters across sub-pages
 * without re-entering them.
 *
 * Written by:
 * - **`productListFilters` store** — full, visibility/forced-gated snapshot on
 *   every filter change (via {@link ProductListFiltersStorage.write}).
 * - **`offerFilters` store** — a partial merge of just the offer-editable fields
 *   (`dateRange`, `roomOccupancy`, `duration`) via
 *   {@link ProductListFiltersStorage.merge}, so offer-page edits also persist.
 *
 * Read by:
 * - **`productListFilters.loadFromLocalStorage`** and
 *   **`useOfferInitializer`** (hydration fallback after the URL).
 *
 * The on-disk shape is `FilterState` serialized with `JSON.stringify`: `Date`
 * objects become ISO strings and are revived by {@link read}. `undefined`
 * fields are dropped by `JSON.stringify`, which is how forced fields are
 * cleared from the blob.
 */

import type { FilterState } from '@/stores/productListFilters';

const KEY = 'ibe_product_list_filters';

/**
 * Revive a parsed blob's `dateRange` (ISO strings → `Date`) in place.
 * Returns `false` when a `dateRange` is present but malformed, so callers that
 * need valid data can reject the whole blob.
 */
function reviveDateRange(parsed: { dateRange?: unknown }): boolean {
  if (!parsed.dateRange) return true;
  if (Array.isArray(parsed.dateRange) && parsed.dateRange.length === 2) {
    parsed.dateRange = [
      new Date(parsed.dateRange[0] as string),
      new Date(parsed.dateRange[1] as string),
    ];
    return true;
  }
  console.warn('[ProductListFiltersStorage] Invalid dateRange format in localStorage');
  return false;
}

export class ProductListFiltersStorage {
  static readonly KEY = KEY;

  /**
   * Read the raw stored blob (dates left as ISO strings), or `{}` when
   * missing/unavailable/malformed. Used as the base for merges, where the value
   * is re-serialized anyway so reviving dates would be wasted work.
   */
  static readRaw(): FilterState {
    let raw: string | null;
    try {
      raw = localStorage.getItem(KEY);
    } catch (error) {
      console.warn('[ProductListFiltersStorage] localStorage unavailable:', error);
      return {};
    }
    if (!raw) return {};
    try {
      return JSON.parse(raw) as FilterState;
    } catch (error) {
      console.warn('[ProductListFiltersStorage] Invalid JSON, ignoring:', error);
      return {};
    }
  }

  /**
   * Read and revive the stored blob into a usable `FilterState`, or `null` when
   * nothing is stored or the `dateRange` is malformed. Used by consumers that
   * hydrate state (they need real `Date` objects).
   */
  static read(): FilterState | null {
    let raw: string | null;
    try {
      raw = localStorage.getItem(KEY);
    } catch (error) {
      console.warn('[ProductListFiltersStorage] localStorage unavailable:', error);
      return null;
    }
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!reviveDateRange(parsed)) return null;
      return parsed as FilterState;
    } catch (error) {
      console.warn('[ProductListFiltersStorage] Failed to load from localStorage:', error);
      return null;
    }
  }

  /**
   * Overwrite the stored blob. `undefined` fields are dropped by
   * `JSON.stringify` (how forced fields are cleared). Silently no-ops when
   * localStorage is unavailable (private browsing / quota).
   */
  static write(state: FilterState): void {
    const persistable: FilterState = { ...state };
    delete persistable.durationMode;

    try {
      localStorage.setItem(KEY, JSON.stringify(persistable));
    } catch (error) {
      console.warn('[ProductListFiltersStorage] Failed to save to localStorage:', error);
    }
  }

  /**
   * Merge only the **defined** keys of `patch` over the existing blob, leaving
   * every other persisted field untouched, then persist. `currentPage` is never
   * stored. Callers must omit fields they don't want to persist (passing
   * `undefined` preserves the existing value rather than clearing it).
   */
  static merge(patch: Partial<FilterState>): void {
    const next = this.readRaw();
    (Object.entries(patch) as [keyof FilterState, unknown][]).forEach(([key, value]) => {
      if (value !== undefined) {
        (next as Record<string, unknown>)[key] = value;
      }
    });
    delete next.currentPage;
    this.write(next);
  }

  /** Remove the entire stored blob. */
  static removeAll(): void {
    try {
      localStorage.removeItem(KEY);
    } catch {
      // Ignore — localStorage may be unavailable
    }
  }

  /** Drop only the persisted period fields (`dateRange`, `duration`). */
  static removePeriod(): void {
    let raw: string | null;
    try {
      raw = localStorage.getItem(KEY);
    } catch {
      return;
    }
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        delete parsed.dateRange;
        delete parsed.duration;
        this.write(parsed as FilterState);
      } else {
        this.removeAll();
      }
    } catch {
      this.removeAll();
    }
  }
}
